import { useState } from 'react'
import {
  Group, Button, Text, Modal, Stack, Card, Avatar, Badge, Alert, Box,
} from '@mantine/core'
import {
  IconUserCheck, IconUserOff, IconRefresh, IconTrash, IconAlertTriangle,
} from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

export interface BulkUserRow {
  id: number
  email: string
  full_name: string
  is_active: boolean
}

interface UsersBulkBarProps {
  selectedIds: Set<number>
  users: BulkUserRow[]
  onClearSelection: () => void
}

function getInitials(name: string): string {
  return name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
}

export function UsersBulkBar({ selectedIds, users, onClearSelection }: UsersBulkBarProps) {
  const qc = useQueryClient()

  const [loading, setLoading] = useState(false)
  const [deleteModalOpened, setDeleteModalOpened] = useState(false)

  const selectedUsers = users.filter((u) => selectedIds.has(u.id))
  const count = selectedIds.size

  if (count === 0) return null

  const handleFinish = (action: string, results: PromiseSettledResult<unknown>[]) => {
    const ok = results.filter((r) => r.status === 'fulfilled').length
    const fail = results.filter((r) => r.status === 'rejected').length
    const color = fail > 0 ? 'orange' : 'teal'
    notifications.show({
      color,
      title: action,
      message: `${ok} sucesso${fail > 0 ? `, ${fail} falha(s)` : ''}`,
    })
    qc.invalidateQueries({ queryKey: ['users'] })
    onClearSelection()
  }

  const bulkActivate = async () => {
    setLoading(true)
    try {
      const results = await Promise.allSettled(
        Array.from(selectedIds).map((id) =>
          api.put(`/v1/users/${id}`, { is_active: true })
        )
      )
      handleFinish('Ativar usuários', results)
    } finally {
      setLoading(false)
    }
  }

  const bulkDeactivate = async () => {
    setLoading(true)
    try {
      const results = await Promise.allSettled(
        Array.from(selectedIds).map((id) =>
          api.put(`/v1/users/${id}`, { is_active: false })
        )
      )
      handleFinish('Desativar usuários', results)
    } finally {
      setLoading(false)
    }
  }

  const bulkSync = async () => {
    setLoading(true)
    try {
      const results = await Promise.allSettled(
        Array.from(selectedIds).map((id) =>
          api.post(`/v1/users/${id}/sync-ldap`, {})
        )
      )
      handleFinish('Sync Kanidm', results)
    } finally {
      setLoading(false)
    }
  }

  const bulkDelete = async () => {
    setLoading(true)
    try {
      const results = await Promise.allSettled(
        Array.from(selectedIds).map((id) =>
          api.delete(`/v1/users/${id}`)
        )
      )
      handleFinish('Remover usuários', results)
      setDeleteModalOpened(false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Box
        px="md"
        py="xs"
        style={{
          background: 'var(--ai-border-light, #e6f3f5)',
          // `#b3dce2` e' o valor de `--ai-border-medium` no tema claro, e estava cravado aqui.
          // O `index.css:94` redefine os dois tokens em `[data-mantine-color-scheme="dark"]`,
          // entao o fundo acompanhava o tema escuro e a borda ficava azul-clara sozinha.
          border: '1px solid var(--ai-border-medium, #b3dce2)',
          borderRadius: 8,
        }}
      >
        <Group justify="space-between">
          <Text size="sm" fw={600}>
            {count} selecionado{count > 1 ? 's' : ''}
          </Text>
          <Group gap="xs">
            <Button
              size="xs"
              variant="light"
              color="teal"
              leftSection={<IconUserCheck size={14} />}
              loading={loading}
              onClick={bulkActivate}
            >
              Ativar
            </Button>
            <Button
              size="xs"
              variant="light"
              color="red"
              leftSection={<IconUserOff size={14} />}
              loading={loading}
              onClick={bulkDeactivate}
            >
              Desativar
            </Button>
            <Button
              size="xs"
              variant="light"
              color="orange"
              leftSection={<IconRefresh size={14} />}
              loading={loading}
              onClick={bulkSync}
            >
              Sync Kanidm
            </Button>
            <Button
              size="xs"
              variant="light"
              color="red"
              leftSection={<IconTrash size={14} />}
              loading={loading}
              onClick={() => setDeleteModalOpened(true)}
            >
              Remover
            </Button>
          </Group>
        </Group>
      </Box>

      <Modal
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        title="Remover usuários em lote"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Ação irreversível">
            <Text size="sm">
              {count} usuário{count > 1 ? 's' : ''} ter{count > 1 ? 'ão' : 'á'} dados pessoais anonimizados,
              sessões encerradas e acesso revogado. Trilhas de auditoria são preservadas.
            </Text>
          </Alert>

          <Stack gap="xs">
            {selectedUsers.map((u) => (
              <Card key={u.id} withBorder p="xs">
                <Group gap="sm">
                  <Avatar size="sm" color="teal" radius="xl">
                    {getInitials(u.full_name || u.email)}
                  </Avatar>
                  <Box>
                    <Text size="sm" fw={500}>{u.full_name || '\u2014'}</Text>
                    <Text size="xs" c="dimmed">{u.email}</Text>
                  </Box>
                </Group>
              </Card>
            ))}
          </Stack>

          <Group justify="flex-end" gap="xs">
            <Button variant="default" size="sm" onClick={() => setDeleteModalOpened(false)}>
              Cancelar
            </Button>
            <Button
              color="red"
              size="sm"
              leftSection={<IconTrash size={14} />}
              loading={loading}
              onClick={bulkDelete}
            >
              Remover {count} usuário{count > 1 ? 's' : ''}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
